/**
 * Palette — the left sidebar. Lists every GI in the Vault, each
 * draggable onto the canvas. Setting data on `dataTransfer` with a
 * recognizable mime lets the canvas's drop handler grab the payload
 * without leaking it to the browser's download UI.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DomainIcon } from '@/components/DomainIcon';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/LoadingState';
import type { GiSummary } from '@/api/types';
import { useVaultSnapshot } from '@/hooks/useVault';
import { normalizeDomain } from '@/utils/format';

export const PALETTE_MIME = 'application/x-gi-engine.palette-drag';

export function Palette({
  onDropNode: _onDropNode,
}: {
  /** Optional callback invoked after a successful canvas drop. The
   *  canvas itself owns the drop handler; this prop is here for future
   *  "just clicked a palette item" affordances. */
  onDropNode?: (summary: GiSummary) => void;
}) {
  const { t } = useTranslation('blueprint');
  const query = useVaultSnapshot();
  const [filter, setFilter] = useState('');

  if (query.isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }
  if (query.isError) {
    return <p className="text-sm text-danger">{(query.error as Error).message}</p>;
  }

  const all = query.data.gis;
  const filtered = filter
    ? all.filter((g) => g.name.toLowerCase().includes(filter.toLowerCase()))
    : all;

  return (
    <div className="flex h-full flex-col gap-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted">
          {t('palette.title')}
        </h2>
        <p className="mt-1 text-xs text-ink-subtle">{t('palette.drag_hint')}</p>
      </div>

      <input
        type="search"
        placeholder={t('palette.filter_placeholder')}
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-ink placeholder:text-ink-subtle focus-visible:border-spark"
      />

      {all.length === 0 ? (
        <EmptyState title={t('palette.empty')} body="" />
      ) : (
        <ul className="flex-1 overflow-auto pe-1">
          {filtered.map((g) => (
            <li key={g.root_id}>
              <PaletteItem gi={g} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PaletteItem({ gi }: { gi: GiSummary }) {
  const normalized = normalizeDomain(gi.domain);
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(PALETTE_MIME, gi.root_id);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      aria-grabbed="true"
      role="button"
      className="mb-2 flex cursor-grab items-center gap-2 rounded-md border border-border bg-surface p-2 text-xs transition-colors hover:border-border-strong hover:bg-surface-raised active:cursor-grabbing"
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-domain-${normalized}-soft text-domain-${normalized}`}
      >
        <DomainIcon domain={normalized} className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink">{gi.name}</p>
        <p className="truncate font-mono text-[10px] text-ink-muted">v{gi.version}</p>
      </span>
    </div>
  );
}
